import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { InventoryExportInput } from '../../types/inventory';
import { formatInventoryExportFilename } from '../../utils/inventoryCsvFilename';

function triggerCsvDownload(content: string, filename: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

async function exportInventory(
    sessionId: string,
    input: InventoryExportInput,
): Promise<void> {
    const response = await axios.post(`${API_URL}/inventory/export`, input, {
        headers: { Authorization: sessionId },
        responseType: 'text',
        transformResponse: [(data) => data],
    });

    let csv: string;
    if (typeof response.data === 'string') {
        const trimmed = response.data.trim();
        if (trimmed.startsWith('{')) {
            try {
                const parsed = JSON.parse(trimmed) as { csv?: string };
                csv = parsed.csv ?? response.data;
            } catch {
                csv = response.data;
            }
        } else {
            csv = response.data;
        }
    } else if (response.data && typeof response.data === 'object' && 'csv' in response.data) {
        csv = String((response.data as { csv: string }).csv);
    } else {
        csv = String(response.data ?? '');
    }

    triggerCsvDownload(csv, formatInventoryExportFilename());
}

export const useExportInventory = (
    options?: UseMutationOptions<void, unknown, InventoryExportInput>,
) => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: InventoryExportInput) =>
            exportInventory(sessionContext?.sessionId ?? '', input),
        ...options,
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: ['/inventory/activity-logs'] });
            options?.onSuccess?.(...args);
        },
    });
};
